import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { catalogProducts, catalogFilterOptions } from '@/mocks/catalog';
import { BLOG_ARTICLES } from '@/mocks/blog';
import { useCart } from '@/hooks/useCart';
import { useFavorites } from '@/hooks/useFavorites';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

/* ─── Constants ─── */
const POPULAR_QUERIES = [
  'Нурофен', 'Но-шпа', 'Омепразол', 'Кардиомагнил', 'Линекс',
  'Витамин D3', 'Омега-3', 'Зодак', 'Терафлю', 'Магне B6',
];

const HISTORY_KEY = 'neonpharma_search_history';
const getHistory = (): string[] => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]'); }
  catch { return []; }
};
const saveHistory = (q: string) => {
  const prev = getHistory().filter(x => x !== q);
  localStorage.setItem(HISTORY_KEY, JSON.stringify([q, ...prev].slice(0, 8)));
};
const clearHistory = () => localStorage.removeItem(HISTORY_KEY);

const CATEGORY_COLORS: Record<string, string> = {
  'Сердце и сосуды': '#ff2d78',
  'Обезболивающие': '#f0ff00',
  'Желудок и кишечник': '#39ff14',
  'Витамины и БАД': '#00f5ff',
  'Простуда и грипп': '#ff6b00',
  'Антибиотики': '#39ff14',
  'Аллергия': '#f0ff00',
};

const ARTICLE_CATEGORY_COLORS: Record<string, string> = {
  'Иммунитет': '#ff2d78',
  'Уход за кожей': '#ff6b00',
  'Витамины и БАД': '#00f5ff',
  'Здоровый сон': '#39ff14',
  'Простуда и грипп': '#f0ff00',
  'Сердце и сосуды': '#ff2d78',
  'Пищеварение': '#39ff14',
  'Детское здоровье': '#00f5ff',
};

const CATEGORY_ICONS: Record<string, string> = {
  'Сердце и сосуды': 'ri-heart-pulse-line',
  'Обезболивающие': 'ri-capsule-line',
  'Желудок и кишечник': 'ri-leaf-line',
  'Витамины и БАД': 'ri-sun-line',
  'Простуда и грипп': 'ri-temp-cold-line',
  'Антибиотики': 'ri-test-tube-line',
  'Аллергия': 'ri-lungs-line',
};

const PAGE_SIZE = 8;

type TabType = 'all' | 'products' | 'articles';
type ViewMode = 'list' | 'grid';
type SortType = 'popular' | 'price_asc' | 'price_desc' | 'rating' | 'discount';

/* ─── Highlight helper ─── */
const HighlightText = ({ text, query }: { text: string; query: string }) => {
  if (!query.trim()) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ background: 'rgba(255,45,120,0.3)', color: '#ff2d78', borderRadius: '2px', padding: '0 1px' }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

/* ─── Suggestion types ─── */
interface SuggestionItem {
  type: 'product' | 'category' | 'substance' | 'article';
  label: string;
  sub?: string;
  id?: number;
  color?: string;
  price?: number;
  image?: string;
  slug?: string;
}

const buildSuggestions = (q: string): SuggestionItem[] => {
  if (!q.trim()) return [];
  const lower = q.toLowerCase();
  const results: SuggestionItem[] = [];

  catalogProducts
    .filter(p =>
      p.name.toLowerCase().includes(lower) ||
      p.activeSubstance.toLowerCase().includes(lower) ||
      p.brand.toLowerCase().includes(lower)
    )
    .slice(0, 4)
    .forEach(p => results.push({
      type: 'product', label: p.name, sub: p.form + ' · ' + p.dosage,
      id: p.id, color: p.color, price: p.price, image: p.image,
    }));

  BLOG_ARTICLES
    .filter(a =>
      a.title.toLowerCase().includes(lower) ||
      a.category.toLowerCase().includes(lower) ||
      a.tags.some(t => t.toLowerCase().includes(lower))
    )
    .slice(0, 2)
    .forEach(a => results.push({
      type: 'article', label: a.title, sub: a.category + ' · ' + a.readTime + ' мин',
      id: a.id, color: ARTICLE_CATEGORY_COLORS[a.category] ?? '#ff2d78', image: a.image, slug: a.slug,
    }));

  const cats = [...new Set(catalogProducts.map(p => p.category))];
  cats.filter(c => c.toLowerCase().includes(lower)).slice(0, 1).forEach(c =>
    results.push({ type: 'category', label: c, color: CATEGORY_COLORS[c] ?? '#ff2d78' })
  );

  const subs = [...new Set(catalogProducts.map(p => p.activeSubstance))];
  subs.filter(s => s.toLowerCase().includes(lower)).slice(0, 1).forEach(s =>
    results.push({ type: 'substance', label: s, sub: 'Действующее вещество' })
  );

  return results.slice(0, 8);
};

/* ─── Skeleton loaders ─── */
const SkeletonList = () => (
  <div className="flex gap-4 p-4 rounded-2xl animate-pulse" style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
    <div className="w-20 h-20 rounded-xl shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }} />
    <div className="flex-1 space-y-2 py-1">
      <div className="h-3 rounded-full w-1/4" style={{ background: 'rgba(255,255,255,0.06)' }} />
      <div className="h-4 rounded-full w-3/4" style={{ background: 'rgba(255,255,255,0.08)' }} />
      <div className="h-3 rounded-full w-1/2" style={{ background: 'rgba(255,255,255,0.05)' }} />
    </div>
    <div className="flex flex-col items-end gap-2 shrink-0">
      <div className="h-6 w-16 rounded-lg" style={{ background: 'rgba(255,255,255,0.07)' }} />
      <div className="h-8 w-24 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
    </div>
  </div>
);

const SkeletonGrid = () => (
  <div className="rounded-2xl overflow-hidden animate-pulse" style={{ border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
    <div className="h-40" style={{ background: 'rgba(255,255,255,0.04)' }} />
    <div className="p-4 space-y-2">
      <div className="h-3 rounded-full w-1/3" style={{ background: 'rgba(255,255,255,0.06)' }} />
      <div className="h-4 rounded-full w-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
      <div className="h-3 rounded-full w-2/3" style={{ background: 'rgba(255,255,255,0.05)' }} />
      <div className="h-8 rounded-xl w-full mt-2" style={{ background: 'rgba(255,255,255,0.05)' }} />
    </div>
  </div>
);

/* ─── Product list card ─── */
const ProductListCard = ({
  p, query, onNavigate, animDelay,
}: {
  p: typeof catalogProducts[0];
  query: string;
  onNavigate: () => void;
  animDelay: number;
}) => {
  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const color = p.color;
  const inFav = isFavorite(p.id);

  return (
    <div
      onClick={() => { saveHistory(p.name); onNavigate(); }}
      className="flex gap-4 p-4 rounded-2xl cursor-pointer group transition-all duration-300"
      style={{
        border: `1px solid ${color}15`,
        background: 'rgba(255,255,255,0.02)',
        animationDelay: `${animDelay}ms`,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}40`; (e.currentTarget as HTMLElement).style.background = `${color}04`; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}15`; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
    >
      <div className="w-20 h-20 shrink-0 rounded-xl flex items-center justify-center overflow-hidden" style={{ background: `linear-gradient(135deg, #0a0a14, ${color}10)` }}>
        <img src={p.image} alt={p.name} className="w-16 h-16 object-contain group-hover:scale-105 transition-transform duration-300" style={{ filter: `drop-shadow(0 4px 8px ${color}40)` }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 mb-1 flex-wrap">
          {p.badge && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] font-bold shrink-0" style={{ background: p.badgeColor, color: '#050508' }}>{p.badge}</span>}
          {p.prescription && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] border shrink-0" style={{ borderColor: '#f0ff0040', color: '#f0ff00' }}>Рецепт</span>}
          <span className="px-2 py-0.5 rounded-full font-mono text-[10px] shrink-0" style={{ background: `${color}12`, color, border: `1px solid ${color}25` }}>{p.category}</span>
        </div>
        <h3 className="font-orbitron font-bold text-sm text-white leading-tight mb-1 line-clamp-2">
          <HighlightText text={p.name} query={query} />
        </h3>
        <p className="font-mono text-xs text-white/35 mb-2 line-clamp-1">{p.description}</p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] text-white/30 px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>{p.form}</span>
          <span className="font-mono text-[10px] text-white/30 px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <HighlightText text={p.brand} query={query} />
          </span>
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-[10px]" style={{ color: '#f0ff00' }} />
            <span className="font-mono text-[10px] text-white/40">{p.rating} ({p.reviews})</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end justify-between shrink-0">
        <div className="text-right">
          <div className="font-orbitron font-black text-lg" style={{ color, textShadow: `0 0 8px ${color}60` }}>{p.price.toLocaleString()}₽</div>
          {p.oldPrice && <div className="font-mono text-xs text-white/25 line-through">{p.oldPrice.toLocaleString()}₽</div>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); toggleFavorite({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color, discount: p.discount, rating: p.rating, inStock: p.inStock }); }}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300"
            style={{ border: inFav ? `1px solid ${color}60` : '1px solid rgba(255,255,255,0.1)', background: inFav ? `${color}15` : 'transparent' }}
          >
            <i className={inFav ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: inFav ? color : 'rgba(255,255,255,0.3)', fontSize: '13px' }} />
          </button>
          <button
            onClick={e => { e.stopPropagation(); addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color }); }}
            className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-full font-mono text-xs cursor-pointer transition-all duration-300"
            style={hasItem(p.id) ? { background: color, color: '#050508', boxShadow: `0 0 10px ${color}` } : { border: `1px solid ${color}`, color }}
          >
            <i className={hasItem(p.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
            {hasItem(p.id) ? 'В корзине' : 'В корзину'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Product grid card ─── */
const ProductGridCard = ({
  p, query, onNavigate, animDelay,
}: {
  p: typeof catalogProducts[0];
  query: string;
  onNavigate: () => void;
  animDelay: number;
}) => {
  const { addItem, hasItem } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const color = p.color;
  const inFav = isFavorite(p.id);

  return (
    <div
      onClick={() => { saveHistory(p.name); onNavigate(); }}
      className="rounded-2xl overflow-hidden cursor-pointer group transition-all duration-300 flex flex-col relative"
      style={{
        border: `1px solid ${color}15`,
        background: 'rgba(255,255,255,0.02)',
        animationDelay: `${animDelay}ms`,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}45`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}15`; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
    >
      {/* Badges */}
      <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
        {p.badge && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] font-bold" style={{ background: p.badgeColor, color: '#050508' }}>{p.badge}</span>}
        {p.prescription && <span className="px-2 py-0.5 rounded-full font-mono text-[10px] border" style={{ borderColor: '#f0ff0040', color: '#f0ff00', background: 'rgba(5,5,8,0.8)' }}>Рецепт</span>}
      </div>
      {/* Fav button */}
      <button
        onClick={e => { e.stopPropagation(); toggleFavorite({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color, discount: p.discount, rating: p.rating, inStock: p.inStock }); }}
        className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300"
        style={{ border: inFav ? `1px solid ${color}60` : '1px solid rgba(255,255,255,0.12)', background: inFav ? `${color}20` : 'rgba(5,5,8,0.7)' }}
      >
        <i className={inFav ? 'ri-heart-fill' : 'ri-heart-line'} style={{ color: inFav ? color : 'rgba(255,255,255,0.4)', fontSize: '13px' }} />
      </button>

      {/* Image */}
      <div className="h-40 flex items-center justify-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, #0a0a14, ${color}08)` }}>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-10 rounded-full opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: color, filter: 'blur(14px)' }} />
        <img src={p.image} alt={p.name} className="w-28 h-28 object-contain relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-1" style={{ filter: `drop-shadow(0 6px 14px ${color}50)` }} />
      </div>

      <div className="p-4 flex flex-col flex-1">
        <span className="font-mono text-[10px] mb-1.5" style={{ color: `${color}90` }}>{p.category}</span>
        <h3 className="font-orbitron font-bold text-xs text-white leading-snug mb-1 line-clamp-2 flex-1">
          <HighlightText text={p.name} query={query} />
        </h3>
        <div className="flex items-center gap-1 mb-3">
          <i className="ri-star-fill text-[10px]" style={{ color: '#f0ff00' }} />
          <span className="font-mono text-[10px] text-white/40">{p.rating} ({p.reviews})</span>
        </div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="font-orbitron font-black text-lg" style={{ color, textShadow: `0 0 8px ${color}60` }}>{p.price.toLocaleString()}₽</div>
            {p.oldPrice && <div className="font-mono text-xs text-white/25 line-through">{p.oldPrice.toLocaleString()}₽</div>}
          </div>
          {p.discount > 0 && (
            <span className="font-mono text-xs font-bold" style={{ color: '#39ff14' }}>-{p.discount}%</span>
          )}
        </div>
        <button
          onClick={e => { e.stopPropagation(); addItem({ id: p.id, name: p.name, price: p.price, oldPrice: p.oldPrice ?? undefined, image: p.image, color }); }}
          className="whitespace-nowrap w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
          style={hasItem(p.id) ? { background: color, color: '#050508', boxShadow: `0 0 12px ${color}` } : { border: `1px solid ${color}`, color }}
        >
          <i className={hasItem(p.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
          {hasItem(p.id) ? 'В корзине' : 'В корзину'}
        </button>
      </div>

      <div className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
    </div>
  );
};

/* ─── Article result card ─── */
const ArticleResultCard = ({ article, query, animDelay }: { article: typeof BLOG_ARTICLES[0]; query: string; animDelay: number }) => {
  const color = ARTICLE_CATEGORY_COLORS[article.category] ?? '#ff2d78';
  const dateStr = new Date(article.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="flex gap-4 p-4 rounded-2xl group transition-all duration-300 block"
      style={{ border: `1px solid ${color}15`, background: 'rgba(255,255,255,0.02)', textDecoration: 'none', animationDelay: `${animDelay}ms` }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}40`; (e.currentTarget as HTMLElement).style.background = `${color}04`; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}15`; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
    >
      <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <span className="px-2 py-0.5 rounded-full font-mono text-[10px] font-bold shrink-0" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
            <i className="ri-article-line mr-1" />{article.category}
          </span>
          <span className="font-mono text-[10px] text-white/25">{dateStr}</span>
        </div>
        <h3 className="font-orbitron font-bold text-sm text-white leading-tight mb-1 line-clamp-2 group-hover:opacity-80 transition-opacity">
          <HighlightText text={article.title} query={query} />
        </h3>
        <p className="font-mono text-xs text-white/35 line-clamp-2">{article.excerpt}</p>
      </div>
      <div className="flex flex-col items-end justify-between shrink-0 ml-2">
        <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: `${color}10`, border: `1px solid ${color}20` }}>
          <i className="ri-time-line text-[10px]" style={{ color }} />
          <span className="font-mono text-[10px]" style={{ color }}>{article.readTime} мин</span>
        </div>
        <div className="flex items-center gap-1 font-mono text-xs text-white/30">
          <span>{article.author.split(' ')[0]}</span>
          <i className="ri-arrow-right-line text-white/20" />
        </div>
      </div>
    </Link>
  );
};

/* ─── Sidebar filters ─── */
interface Filters {
  categories: string[];
  brands: string[];
  forms: string[];
  countries: string[];
  priceMin: string;
  priceMax: string;
  onlyInStock: boolean;
  onlyDiscount: boolean;
  onlyPrescription: boolean;
}

const defaultFilters = (): Filters => ({
  categories: [], brands: [], forms: [], countries: [],
  priceMin: '', priceMax: '',
  onlyInStock: false, onlyDiscount: false, onlyPrescription: false,
});

interface FilterSectionProps {
  title: string;
  icon: string;
  items: string[];
  selected: string[];
  onToggle: (v: string) => void;
  colorMap?: Record<string, string>;
  collapsed?: boolean;
}

const FilterSection = ({ title, icon, items, selected, onToggle, colorMap, collapsed: initCollapsed = false }: FilterSectionProps) => {
  const [open, setOpen] = useState(!initCollapsed);
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <i className={`${icon} text-sm`} style={{ color: 'rgba(255,45,120,0.6)' }} />
          <span className="font-mono text-xs text-white/60 group-hover:text-white/80 transition-colors">{title}</span>
          {selected.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: '#ff2d78', color: '#fff' }}>{selected.length}</span>
          )}
        </div>
        {open
          ? <i className="ri-arrow-up-s-line text-sm text-white/30" />
          : <i className="ri-arrow-down-s-line text-sm text-white/30" />
        }
      </button>
      {open && (
        <div className="pb-3 flex flex-wrap gap-1.5">
          {items.map(item => {
            const active = selected.includes(item);
            const color = colorMap?.[item] ?? '#ff2d78';
            return (
              <button
                key={item}
                onClick={() => onToggle(item)}
                className="whitespace-nowrap px-2.5 py-1 rounded-full font-mono text-[11px] cursor-pointer transition-all duration-200"
                style={active
                  ? { background: `${color}20`, border: `1px solid ${color}60`, color }
                  : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)' }
                }
              >
                {item}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SearchSidebar = ({
  filters, onChange, resultCategories, totalCount,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  resultCategories: string[];
  totalCount: number;
}) => {
  const toggle = (key: keyof Pick<Filters, 'categories' | 'brands' | 'forms' | 'countries'>, val: string) => {
    const arr = filters[key] as string[];
    onChange({ ...filters, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };

  const hasActive = filters.categories.length > 0 || filters.brands.length > 0 || filters.forms.length > 0 ||
    filters.countries.length > 0 || filters.priceMin || filters.priceMax ||
    filters.onlyInStock || filters.onlyDiscount || filters.onlyPrescription;

  return (
    <aside className="w-64 shrink-0 hidden lg:block">
      <div className="sticky top-24 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,45,120,0.04)' }}>
          <div className="flex items-center gap-2">
            <i className="ri-equalizer-3-line" style={{ color: '#ff2d78' }} />
            <span className="font-orbitron font-bold text-xs text-white">Фильтры</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-white/30">{totalCount} товаров</span>
            {hasActive && (
              <button
                onClick={() => onChange(defaultFilters())}
                className="font-mono text-[10px] cursor-pointer transition-colors"
                style={{ color: '#ff2d78' }}
              >
                Сбросить
              </button>
            )}
          </div>
        </div>

        <div className="px-4">
          {/* Price range */}
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="py-3">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-price-tag-3-line text-sm" style={{ color: 'rgba(255,45,120,0.6)' }} />
                <span className="font-mono text-xs text-white/60">Цена, ₽</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={filters.priceMin}
                  onChange={e => onChange({ ...filters, priceMin: e.target.value })}
                  placeholder="от"
                  className="w-full px-3 py-2 rounded-lg font-mono text-xs text-white bg-transparent outline-none"
                  style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                />
                <span className="font-mono text-xs text-white/30 shrink-0">—</span>
                <input
                  type="number"
                  value={filters.priceMax}
                  onChange={e => onChange({ ...filters, priceMax: e.target.value })}
                  placeholder="до"
                  className="w-full px-3 py-2 rounded-lg font-mono text-xs text-white bg-transparent outline-none"
                  style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              { label: 'В наличии', key: 'onlyInStock' as const, icon: 'ri-checkbox-circle-line' },
              { label: 'Со скидкой', key: 'onlyDiscount' as const, icon: 'ri-percent-line' },
              { label: 'Без рецепта', key: 'onlyPrescription' as const, icon: 'ri-file-list-3-line' },
            ].map(({ label, key, icon }) => (
              <button
                key={key}
                onClick={() => onChange({ ...filters, [key]: !filters[key] })}
                className="w-full flex items-center justify-between py-1.5 cursor-pointer group"
              >
                <div className="flex items-center gap-2">
                  <i className={`${icon} text-sm`} style={{ color: filters[key] ? '#ff2d78' : 'rgba(255,255,255,0.3)' }} />
                  <span className="font-mono text-xs text-white/50 group-hover:text-white/70 transition-colors">{label}</span>
                </div>
                <div className="w-8 h-4 rounded-full transition-all duration-300 relative shrink-0" style={{ background: filters[key] ? '#ff2d78' : 'rgba(255,255,255,0.1)' }}>
                  <div className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all duration-300" style={{ left: filters[key] ? '17px' : '2px' }} />
                </div>
              </button>
            ))}
          </div>

          {/* Category */}
          <FilterSection
            title="Категория"
            icon="ri-grid-line"
            items={resultCategories.length > 0 ? resultCategories : catalogFilterOptions.categories}
            selected={filters.categories}
            onToggle={v => toggle('categories', v)}
            colorMap={CATEGORY_COLORS}
          />

          {/* Brand */}
          <FilterSection
            title="Бренд"
            icon="ri-building-line"
            items={catalogFilterOptions.brands}
            selected={filters.brands}
            onToggle={v => toggle('brands', v)}
            collapsed
          />

          {/* Form */}
          <FilterSection
            title="Форма выпуска"
            icon="ri-capsule-line"
            items={catalogFilterOptions.forms}
            selected={filters.forms}
            onToggle={v => toggle('forms', v)}
            collapsed
          />

          {/* Country */}
          <FilterSection
            title="Страна"
            icon="ri-map-pin-line"
            items={catalogFilterOptions.countries}
            selected={filters.countries}
            onToggle={v => toggle('countries', v)}
            collapsed
          />
        </div>
      </div>
    </aside>
  );
};

const searchJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NeonPharma',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

/* ─── Main page ─── */
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const initialQ = searchParams.get('q') ?? '';
  const [inputValue, setInputValue] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);
  const [liveQuery, setLiveQuery] = useState(initialQ);
  const [focused, setFocused] = useState(false);
  const [history, setHistory] = useState<string[]>(getHistory);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortBy, setSortBy] = useState<SortType>('popular');
  const [filters, setFilters] = useState<Filters>(defaultFilters());
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIdx, setSelectedSuggestionIdx] = useState(-1);
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  /* Live search debounce */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue.trim()) {
      setLiveQuery('');
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      setLiveQuery(inputValue.trim());
      setIsSearching(false);
    }, 280);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  const suggestions = useMemo(() => buildSuggestions(inputValue), [inputValue]);
  const activeQuery = liveQuery || query;

  /* Reset page on filter/sort change */
  useEffect(() => { setPage(1); }, [filters, sortBy, activeTab, activeQuery]);

  const allProductResults = useMemo(() => {
    if (!activeQuery.trim()) return [];
    const lower = activeQuery.toLowerCase();
    let list = catalogProducts.filter(p =>
      p.name.toLowerCase().includes(lower) ||
      p.description.toLowerCase().includes(lower) ||
      p.activeSubstance.toLowerCase().includes(lower) ||
      p.brand.toLowerCase().includes(lower) ||
      p.category.toLowerCase().includes(lower) ||
      p.manufacturer.toLowerCase().includes(lower)
    );
    if (filters.categories.length > 0) list = list.filter(p => filters.categories.includes(p.category));
    if (filters.brands.length > 0) list = list.filter(p => filters.brands.includes(p.brand));
    if (filters.forms.length > 0) list = list.filter(p => filters.forms.includes(p.form));
    if (filters.countries.length > 0) list = list.filter(p => filters.countries.includes(p.country));
    if (filters.onlyInStock) list = list.filter(p => p.inStock);
    if (filters.onlyDiscount) list = list.filter(p => p.discount > 0);
    if (filters.onlyPrescription) list = list.filter(p => !p.prescription);
    if (filters.priceMin) list = list.filter(p => p.price >= Number(filters.priceMin));
    if (filters.priceMax) list = list.filter(p => p.price <= Number(filters.priceMax));
    switch (sortBy) {
      case 'price_asc': return [...list].sort((a, b) => a.price - b.price);
      case 'price_desc': return [...list].sort((a, b) => b.price - a.price);
      case 'rating': return [...list].sort((a, b) => b.rating - a.rating);
      case 'discount': return [...list].sort((a, b) => b.discount - a.discount);
      default: return [...list].sort((a, b) => b.reviews - a.reviews);
    }
  }, [activeQuery, sortBy, filters]);

  const articleResults = useMemo(() => {
    if (!activeQuery.trim()) return [];
    const lower = activeQuery.toLowerCase();
    return BLOG_ARTICLES.filter(a =>
      a.title.toLowerCase().includes(lower) ||
      a.excerpt.toLowerCase().includes(lower) ||
      a.category.toLowerCase().includes(lower) ||
      a.tags.some(t => t.toLowerCase().includes(lower)) ||
      a.author.toLowerCase().includes(lower)
    );
  }, [activeQuery]);

  const resultCategories = useMemo(() => {
    if (!activeQuery.trim()) return [];
    const lower = activeQuery.toLowerCase();
    const cats = catalogProducts
      .filter(p => p.name.toLowerCase().includes(lower) || p.activeSubstance.toLowerCase().includes(lower) || p.brand.toLowerCase().includes(lower))
      .map(p => p.category);
    return [...new Set(cats)];
  }, [activeQuery]);

  /* Paginated products */
  const productResults = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return allProductResults.slice(start, start + PAGE_SIZE);
  }, [allProductResults, page]);

  const totalPages = Math.ceil(allProductResults.length / PAGE_SIZE);
  const totalResults = allProductResults.length + articleResults.length;

  const hasActiveFilters = filters.categories.length > 0 || filters.brands.length > 0 || filters.forms.length > 0 ||
    filters.countries.length > 0 || filters.priceMin || filters.priceMax ||
    filters.onlyInStock || filters.onlyDiscount || filters.onlyPrescription;

  const handleSearch = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    saveHistory(trimmed);
    setHistory(getHistory());
    setQuery(trimmed);
    setLiveQuery(trimmed);
    setInputValue(trimmed);
    setSearchParams({ q: trimmed });
    setFocused(false);
    setSelectedSuggestionIdx(-1);
    setFilters(defaultFilters());
    setActiveTab('all');
    setPage(1);
  }, [setSearchParams]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      if (selectedSuggestionIdx >= 0 && suggestions[selectedSuggestionIdx]) {
        handleSuggestionClick(suggestions[selectedSuggestionIdx]);
      } else {
        handleSearch(inputValue);
      }
    } else if (e.key === 'Escape') {
      setFocused(false);
      setSelectedSuggestionIdx(-1);
    }
  };

  const handleSuggestionClick = (s: SuggestionItem) => {
    if (s.type === 'product' && s.id) {
      saveHistory(s.label);
      setHistory(getHistory());
      navigate(`/product/${s.id}`);
    } else if (s.type === 'article' && s.slug) {
      saveHistory(s.label);
      setHistory(getHistory());
      navigate(`/blog/${s.slug}`);
    } else {
      handleSearch(s.label);
    }
  };

  const handleClearHistory = () => { clearHistory(); setHistory([]); };

  const showDropdown = focused && inputValue.length > 0 && suggestions.length > 0;
  const showEmpty = focused && inputValue.length === 0;

  const getSuggestionIcon = (type: SuggestionItem['type']) => {
    switch (type) {
      case 'product': return 'ri-medicine-bottle-line';
      case 'article': return 'ri-article-line';
      case 'category': return 'ri-grid-line';
      case 'substance': return 'ri-test-tube-line';
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title={query ? `Поиск: ${query} — NeonPharma` : 'Поиск лекарств и статей — NeonPharma'}
        description={query ? `Результаты поиска по запросу «${query}» в интернет-аптеке NeonPharma. Лекарства, витамины, БАД и статьи о здоровье.` : 'Поиск лекарств, витаминов, БАД и статей о здоровье в интернет-аптеке NeonPharma. Более 10 000 препаратов.'}
        keywords="поиск лекарств, найти препарат, аптека онлайн, NeonPharma поиск"
        canonical="/search"
        jsonLd={searchJsonLd}
      />
      <Navbar />

      {/* Search hero */}
      <section className="pt-28 pb-10 px-4 md:px-8 lg:px-12 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-48 opacity-10 rounded-full" style={{ background: '#ff2d78', filter: 'blur(80px)' }} />
        <div className="absolute top-20 right-1/4 w-64 h-64 opacity-5 rounded-full" style={{ background: '#00f5ff', filter: 'blur(80px)' }} />
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-6">
            <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color: '#ff2d78' }}>Поиск</span>
          </div>

          <h1 className="font-orbitron font-black text-3xl md:text-4xl text-white mb-8 text-center">
            ПОИСК <span style={{ color: '#ff2d78', textShadow: '0 0 20px rgba(255,45,120,0.5)' }}>ЛЕКАРСТВ</span>
          </h1>

          {/* Search input */}
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center gap-3 px-5 py-4 rounded-2xl transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `2px solid ${focused ? '#ff2d78' : 'rgba(255,255,255,0.1)'}`,
                boxShadow: focused ? '0 0 30px rgba(255,45,120,0.15), 0 0 0 1px rgba(255,45,120,0.1)' : 'none',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                {isSearching ? (
                  <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#ff2d78', borderTopColor: 'transparent' }} />
                ) : (
                  <i className="ri-search-line text-lg" style={{ color: focused ? '#ff2d78' : 'rgba(255,255,255,0.3)' }} />
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => { setInputValue(e.target.value); setSelectedSuggestionIdx(-1); }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => { setFocused(false); setSelectedSuggestionIdx(-1); }, 180)}
                onKeyDown={handleKeyDown}
                placeholder="Название, действующее вещество, статья..."
                className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder-white/20"
              />
              {inputValue && (
                <button
                  onClick={() => { setInputValue(''); setQuery(''); setLiveQuery(''); setSearchParams({}); inputRef.current?.focus(); }}
                  className="w-5 h-5 flex items-center justify-center cursor-pointer text-white/30 hover:text-white/60 transition-colors shrink-0"
                >
                  <i className="ri-close-line" />
                </button>
              )}
              <button
                onClick={() => handleSearch(inputValue)}
                className="whitespace-nowrap px-5 py-2 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all duration-300 shrink-0"
                style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px rgba(255,45,120,0.4)' }}
              >
                НАЙТИ
              </button>
            </div>

            {/* Autocomplete dropdown */}
            {showDropdown && (
              <div
                className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden z-50"
                style={{
                  background: '#0d0d1a',
                  border: '1px solid rgba(255,45,120,0.25)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,45,120,0.05)',
                }}
              >
                <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,45,120,0.04)' }}>
                  <span className="font-mono text-[10px] tracking-widest text-white/25">ПОДСКАЗКИ</span>
                  <span className="font-mono text-[10px] text-white/20">{suggestions.length} результатов</span>
                </div>

                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onMouseDown={() => handleSuggestionClick(s)}
                    onMouseEnter={() => setSelectedSuggestionIdx(i)}
                    className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-150 text-left"
                    style={{
                      borderBottom: i < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                      background: selectedSuggestionIdx === i ? 'rgba(255,45,120,0.08)' : 'transparent',
                    }}
                  >
                    {(s.type === 'product' || s.type === 'article') && s.image ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 flex items-center justify-center" style={{ background: `${s.color}10` }}>
                        <img src={s.image} alt="" className={`${s.type === 'article' ? 'w-full h-full object-cover object-top' : 'w-8 h-8 object-contain'}`} />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${s.color ?? '#ff2d78'}15`, border: `1px solid ${s.color ?? '#ff2d78'}25` }}>
                        <i className={getSuggestionIcon(s.type)} style={{ color: s.color ?? '#ff2d78', fontSize: '16px' }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-white/85 truncate">
                        <HighlightText text={s.label} query={inputValue} />
                      </div>
                      {s.sub && <div className="font-mono text-[11px] text-white/35 truncate">{s.sub}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.price && <span className="font-orbitron font-bold text-sm" style={{ color: s.color }}>{s.price.toLocaleString()}₽</span>}
                      <span className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${s.color ?? '#ff2d78'}15`, color: s.color ?? '#ff2d78', border: `1px solid ${s.color ?? '#ff2d78'}25` }}>
                        {s.type === 'product' ? 'Товар' : s.type === 'article' ? 'Статья' : s.type === 'category' ? 'Категория' : 'Вещество'}
                      </span>
                      {selectedSuggestionIdx === i && <i className="ri-corner-down-left-line text-white/20 text-xs" />}
                    </div>
                  </button>
                ))}

                <div className="px-4 py-2 flex items-center gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.01)' }}>
                  {[['↑↓', 'навигация'], ['Enter', 'выбрать'], ['Esc', 'закрыть']].map(([key, label]) => (
                    <span key={key} className="flex items-center gap-1 font-mono text-[10px] text-white/20">
                      <kbd className="px-1.5 py-0.5 rounded text-[9px]" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>{key}</kbd>
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state dropdown */}
            {showEmpty && (
              <div
                className="absolute top-full left-0 right-0 mt-2 rounded-2xl overflow-hidden z-50 p-4"
                style={{ background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' }}
              >
                {history.length > 0 && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-white/30">
                        <i className="ri-history-line" />
                        ИСТОРИЯ ПОИСКА
                      </span>
                      <button onClick={handleClearHistory} className="flex items-center gap-1 font-mono text-[10px] text-white/25 hover:text-white/50 cursor-pointer transition-colors">
                        <i className="ri-delete-bin-line" />
                        Очистить
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {history.map((h, i) => (
                        <button
                          key={i}
                          onMouseDown={() => handleSearch(h)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                          style={{ background: 'rgba(255,45,120,0.04)', border: '1px solid rgba(255,45,120,0.08)', color: 'rgba(255,255,255,0.55)' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)'; }}
                        >
                          <i className="ri-time-line text-white/30" />
                          {h}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-white/30 mb-3">
                    <i className="ri-fire-line" style={{ color: '#ff2d78' }} />
                    ПОПУЛЯРНЫЕ ЗАПРОСЫ
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_QUERIES.map((q, i) => (
                      <button
                        key={i}
                        onMouseDown={() => handleSearch(q)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                        style={{ background: 'rgba(255,45,120,0.06)', border: '1px solid rgba(255,45,120,0.2)', color: '#ff2d78' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.15)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.06)'; }}
                      >
                        <i className="ri-fire-line" />
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Popular tags */}
          {!activeQuery && (
            <div className="mt-8 flex flex-wrap gap-2 justify-center">
              <span className="font-mono text-xs text-white/30 mr-1 self-center">Популярно:</span>
              {POPULAR_QUERIES.slice(0, 6).map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSearch(q)}
                  className="px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all duration-200"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Results section */}
      {activeQuery ? (
        <section className="pb-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex gap-8">

              {/* Sidebar */}
              {(activeTab === 'all' || activeTab === 'products') && (
                <SearchSidebar
                  filters={filters}
                  onChange={setFilters}
                  resultCategories={resultCategories}
                  totalCount={allProductResults.length}
                />
              )}

              {/* Main content */}
              <div className="flex-1 min-w-0">

                {/* Tabs */}
                <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                  <div className="flex items-center gap-1 p-1 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {([
                      { id: 'all', label: 'Все', count: totalResults, icon: 'ri-search-line' },
                      { id: 'products', label: 'Товары', count: allProductResults.length, icon: 'ri-medicine-bottle-line' },
                      { id: 'articles', label: 'Статьи', count: articleResults.length, icon: 'ri-article-line' },
                    ] as { id: TabType; label: string; count: number; icon: string }[]).map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                        style={activeTab === tab.id
                          ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 12px rgba(255,45,120,0.4)' }
                          : { color: 'rgba(255,255,255,0.4)' }
                        }
                      >
                        <i className={tab.icon} />
                        {tab.label}
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={activeTab === tab.id ? { background: 'rgba(255,255,255,0.25)', color: '#fff' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>
                          {tab.count}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* View + sort controls */}
                  {(activeTab === 'all' || activeTab === 'products') && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Mobile filters btn */}
                      <button
                        onClick={() => setMobileFiltersOpen(o => !o)}
                        className="whitespace-nowrap lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all"
                        style={mobileFiltersOpen || hasActiveFilters
                          ? { background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)', color: '#ff2d78' }
                          : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
                        }
                      >
                        <i className="ri-equalizer-line" />
                        Фильтры
                        {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                      </button>

                      {/* Sort */}
                      <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {([
                          { id: 'popular', label: 'Популярные', icon: 'ri-fire-line' },
                          { id: 'price_asc', label: 'Дешевле', icon: 'ri-sort-asc' },
                          { id: 'price_desc', label: 'Дороже', icon: 'ri-sort-desc' },
                          { id: 'rating', label: 'Рейтинг', icon: 'ri-star-line' },
                          { id: 'discount', label: 'Скидка', icon: 'ri-percent-line' },
                        ] as { id: SortType; label: string; icon: string }[]).map(s => (
                          <button
                            key={s.id}
                            onClick={() => setSortBy(s.id)}
                            className="whitespace-nowrap flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-mono text-[11px] cursor-pointer transition-all duration-200"
                            style={sortBy === s.id
                              ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 8px rgba(255,45,120,0.4)' }
                              : { color: 'rgba(255,255,255,0.35)' }
                            }
                          >
                            <i className={s.icon} />
                            <span className="hidden md:inline">{s.label}</span>
                          </button>
                        ))}
                      </div>

                      {/* View mode */}
                      <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        {([
                          { id: 'list', icon: 'ri-list-check' },
                          { id: 'grid', icon: 'ri-grid-line' },
                        ] as { id: ViewMode; icon: string }[]).map(v => (
                          <button
                            key={v.id}
                            onClick={() => setViewMode(v.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all duration-200"
                            style={viewMode === v.id
                              ? { background: 'rgba(255,45,120,0.2)', color: '#ff2d78' }
                              : { color: 'rgba(255,255,255,0.3)' }
                            }
                          >
                            <i className={v.icon} />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile filters panel */}
                {mobileFiltersOpen && (activeTab === 'all' || activeTab === 'products') && (
                  <div className="lg:hidden mb-5 p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,45,120,0.15)' }}>
                    <SearchSidebar
                      filters={filters}
                      onChange={setFilters}
                      resultCategories={resultCategories}
                      totalCount={allProductResults.length}
                    />
                  </div>
                )}

                {/* Results header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-orbitron font-bold text-base text-white">
                      По запросу <span style={{ color: '#ff2d78' }}>&laquo;{activeQuery}&raquo;</span>
                    </h2>
                    <p className="font-mono text-xs text-white/35 mt-0.5">
                      {isSearching ? (
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 rounded-full border border-t-transparent animate-spin inline-block" style={{ borderColor: '#ff2d78', borderTopColor: 'transparent' }} />
                          Поиск...
                        </span>
                      ) : (
                        <>
                          {activeTab === 'all' && <><span style={{ color: '#ff2d78' }}>{totalResults}</span> результатов</>}
                          {activeTab === 'products' && <><span style={{ color: '#ff2d78' }}>{allProductResults.length}</span> препаратов</>}
                          {activeTab === 'articles' && <><span style={{ color: '#ff2d78' }}>{articleResults.length}</span> статей</>}
                        </>
                      )}
                    </p>
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={() => setFilters(defaultFilters())}
                      className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all"
                      style={{ border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}
                    >
                      <i className="ri-refresh-line" />
                      Сбросить фильтры
                    </button>
                  )}
                </div>

                {/* Skeleton */}
                {isSearching && (
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 gap-4' : 'space-y-3'}>
                    {[0, 1, 2, 3].map(i => viewMode === 'grid' ? <SkeletonGrid key={i} /> : <SkeletonList key={i} />)}
                  </div>
                )}

                {/* No results */}
                {!isSearching && totalResults === 0 && (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-2xl mb-5" style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.2)' }}>
                      <i className="ri-search-line text-3xl" style={{ color: '#ff2d78' }} />
                    </div>
                    <h3 className="font-orbitron font-bold text-xl text-white mb-2">Ничего не найдено</h3>
                    <p className="font-mono text-sm text-white/40 mb-6 max-w-sm">По запросу &laquo;{activeQuery}&raquo; ничего не найдено. Попробуйте другой запрос или сбросьте фильтры.</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="font-mono text-xs text-white/30 self-center">Попробуйте:</span>
                      {POPULAR_QUERIES.slice(0, 4).map((q, i) => (
                        <button key={i} onClick={() => handleSearch(q)} className="px-3 py-1.5 rounded-full font-mono text-xs cursor-pointer transition-all" style={{ background: 'rgba(255,45,120,0.08)', border: '1px solid rgba(255,45,120,0.25)', color: '#ff2d78' }}>{q}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Results */}
                {!isSearching && totalResults > 0 && (
                  <div className="space-y-8">
                    {/* Products */}
                    {(activeTab === 'all' || activeTab === 'products') && allProductResults.length > 0 && (
                      <div>
                        {activeTab === 'all' && (
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <div className="w-1 h-5 rounded-full" style={{ background: '#ff2d78' }} />
                              <span className="font-orbitron font-bold text-sm text-white">Препараты</span>
                              <span className="font-mono text-xs text-white/30">({allProductResults.length})</span>
                            </div>
                            {allProductResults.length > 3 && (
                              <button onClick={() => setActiveTab('products')} className="font-mono text-xs cursor-pointer transition-colors" style={{ color: '#ff2d78' }}>
                                Все препараты <i className="ri-arrow-right-line" />
                              </button>
                            )}
                          </div>
                        )}

                        {viewMode === 'grid' ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {(activeTab === 'all' ? allProductResults.slice(0, 6) : productResults).map((p, idx) => (
                              <ProductGridCard
                                key={p.id}
                                p={p}
                                query={activeQuery}
                                onNavigate={() => navigate(`/product/${p.id}`)}
                                animDelay={idx * 50}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {(activeTab === 'all' ? allProductResults.slice(0, 4) : productResults).map((p, idx) => (
                              <ProductListCard
                                key={p.id}
                                p={p}
                                query={activeQuery}
                                onNavigate={() => navigate(`/product/${p.id}`)}
                                animDelay={idx * 50}
                              />
                            ))}
                          </div>
                        )}

                        {/* Pagination — only in products tab */}
                        {activeTab === 'products' && totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 mt-8">
                            <button
                              onClick={() => setPage(p => Math.max(1, p - 1))}
                              disabled={page === 1}
                              className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                            >
                              <i className="ri-arrow-left-s-line" />
                            </button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                              <button
                                key={p}
                                onClick={() => setPage(p)}
                                className="w-9 h-9 flex items-center justify-center rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                                style={page === p
                                  ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 12px rgba(255,45,120,0.5)' }
                                  : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
                                }
                              >
                                {p}
                              </button>
                            ))}

                            <button
                              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                              disabled={page === totalPages}
                              className="w-9 h-9 flex items-center justify-center rounded-xl cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                              style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                            >
                              <i className="ri-arrow-right-s-line" />
                            </button>

                            <span className="font-mono text-xs text-white/30 ml-2">
                              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, allProductResults.length)} из {allProductResults.length}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Articles */}
                    {(activeTab === 'all' || activeTab === 'articles') && articleResults.length > 0 && (
                      <div>
                        {activeTab === 'all' && (
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <div className="w-1 h-5 rounded-full" style={{ background: '#00f5ff' }} />
                              <span className="font-orbitron font-bold text-sm text-white">Статьи о здоровье</span>
                              <span className="font-mono text-xs text-white/30">({articleResults.length})</span>
                            </div>
                            {articleResults.length > 3 && (
                              <button onClick={() => setActiveTab('articles')} className="font-mono text-xs cursor-pointer transition-colors" style={{ color: '#00f5ff' }}>
                                Все статьи <i className="ri-arrow-right-line" />
                              </button>
                            )}
                          </div>
                        )}
                        <div className="space-y-3">
                          {(activeTab === 'all' ? articleResults.slice(0, 3) : articleResults).map((a, idx) => (
                            <ArticleResultCard key={a.id} article={a} query={activeQuery} animDelay={idx * 50} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Footer links */}
                {!isSearching && totalResults > 0 && (
                  <div className="mt-10 flex flex-wrap gap-4 justify-center">
                    <Link to="/catalog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-sm cursor-pointer transition-all duration-300" style={{ border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                      <i className="ri-grid-line" />
                      Весь каталог
                    </Link>
                    <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-mono text-sm cursor-pointer transition-all duration-300" style={{ border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}>
                      <i className="ri-article-line" />
                      Все статьи
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : (
        /* Empty state — no query */
        <section className="pb-20 px-4 md:px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">

            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { val: `${catalogProducts.length}`, label: 'Препаратов', icon: 'ri-medicine-bottle-line', color: '#ff2d78' },
                { val: `${BLOG_ARTICLES.length}`, label: 'Статей', icon: 'ri-article-line', color: '#00f5ff' },
                { val: `${catalogFilterOptions.categories.length}`, label: 'Категорий', icon: 'ri-grid-line', color: '#39ff14' },
                { val: `${catalogFilterOptions.brands.length}`, label: 'Брендов', icon: 'ri-building-line', color: '#f0ff00' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: `${s.color}06`, border: `1px solid ${s.color}18` }}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}>
                    <i className={s.icon} style={{ color: s.color, fontSize: '18px' }} />
                  </div>
                  <div>
                    <div className="font-orbitron font-black text-xl" style={{ color: s.color }}>{s.val}</div>
                    <div className="font-mono text-[11px] text-white/40">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Categories */}
            <p className="font-mono text-[10px] tracking-widest text-white/25 mb-5 text-center">КАТЕГОРИИ ТОВАРОВ</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
              {catalogFilterOptions.categories.map(cat => {
                const color = CATEGORY_COLORS[cat] ?? '#ff2d78';
                const count = catalogProducts.filter(p => p.category === cat).length;
                const icon = CATEGORY_ICONS[cat] ?? 'ri-medicine-bottle-line';
                return (
                  <button
                    key={cat}
                    onClick={() => handleSearch(cat)}
                    className="flex flex-col items-center gap-3 p-5 rounded-2xl cursor-pointer transition-all duration-300 group"
                    style={{ background: `${color}06`, border: `1px solid ${color}18` }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}45`; (e.currentTarget as HTMLElement).style.background = `${color}10`; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${color}18`; (e.currentTarget as HTMLElement).style.background = `${color}06`; }}
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                      <i className={icon} style={{ color, fontSize: '22px' }} />
                    </div>
                    <div className="text-center">
                      <div className="font-mono text-xs text-white/70 leading-snug">{cat}</div>
                      <div className="font-mono text-[10px] text-white/30 mt-0.5">{count} препаратов</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Popular articles */}
            <p className="font-mono text-[10px] tracking-widest text-white/25 mb-5 text-center">ПОПУЛЯРНЫЕ СТАТЬИ</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {BLOG_ARTICLES.slice(0, 4).map((a, idx) => (
                <ArticleResultCard key={a.id} article={a} query="" animDelay={idx * 60} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default SearchPage;
